import { ESdChangeReason, IApiMethods, IAuthorizationDto, IProjectRole, SpecialDIIds } from "@kuika/kuika-cl-model";
import { DomainService } from "../../../../../services/domain-service";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { DeleteModal } from "../../../../common/delete-modal";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu from "../../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEmptyState from "../../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaPopconfirm from "../../../../ui-kit/kuika-popconfirm/kuika-popconfirm";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSpinnerSmall from "../../../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import "./authorization-panel.scss";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import { RoleManagementModal } from "./role-management-modal";
import { RoleModal } from "./role-modal";

declare let window: any;

export interface IAuthorizationPanelState {
  triggerScreenDeleteModal: boolean;
  isRoleModalActive: boolean;
  roleToDeleteId: string;
  roleToDeleteName: string;
  activeRoleId: string;
  activeRoleName: string;
  activeStartingScreenId: string;
  searchRoleKeyWords: string[];
  searchRoleText: string;
  isLoading: boolean;
  isDeleteRolePopConfirmVisible: boolean;
  activeEditDeletePopConfirmRoleId: string | null;
  projectsThatUsedSameRoleName?: string[];
  /** The Edit dialog is open and still asking which other projects share the role's name. */
  isCheckingRoleUsage: boolean;
  /** Other projects that use the role being edited (undefined until known, or when the check failed). */
  roleUsageProjects?: string[];
  roleUsageCheckFailed: boolean;
  isRolesDropdownOpen: boolean;
  isAccessManagementModalActive: boolean;
  expandedFolderIds: string[];
}

export interface IAuthorizationPanelProps {}

class AuthorizationPanel extends PureSdComponent<IAuthorizationPanelProps & IWithSDProps, IAuthorizationPanelState> {
  constructor(props: IAuthorizationPanelProps & IWithSDProps) {
    super(props);
    this.state = {
      triggerScreenDeleteModal: false,
      isRoleModalActive: false,
      roleToDeleteId: "",
      roleToDeleteName: "",
      activeRoleId: "",
      activeRoleName: "",
      activeStartingScreenId: "",
      searchRoleKeyWords: [],
      searchRoleText: "",
      isLoading: false,
      isDeleteRolePopConfirmVisible: false,
      activeEditDeletePopConfirmRoleId: null,
      isCheckingRoleUsage: false,
      roleUsageCheckFailed: false,
      isRolesDropdownOpen: false,
      isAccessManagementModalActive: false,
      expandedFolderIds: this.getAllFolderIds((props.sd?.project as any)?.roleFolders || [])
    };
  }

  isScreen = (): boolean => {
    return this.getKuikaId() === SpecialDIIds.rootDI.toString();
  };

  getKuikaId = () => {
    return this.props.sd.selectedKuikaIds[0];
  };

  authorizedRolesChanged = (role, event: any) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();
    if (api && apiMethod) {
      this.props.sd.updateApiAuthorization(role);
    } else {
      this.props.sd.updateScreenAuthorization(role);
      if (!this.isHidden()) this.isHiddenChanged();
    }
  };

  isHiddenChanged = () => {
    this.props.sd.isHiddenChanged();
  };

  isDisabledChanged = () => {
    this.props.sd.isDisabledChanged();
  };

  allowAllRolesChanged = () => {
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();
    if (api && apiMethod) {
      this.props.sd.allowApiAllRolesChanged();
    } else {
      this.props.sd.allowAllRolesChanged();
    }
  };

  allowAnonymousAccessChanged = () => {
    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();
    if (api && apiMethod) {
      this.props.sd.allowApiAnonymousAccessChanged();
    } else {
      this.props.sd.allowAnonymousAccessChanged();
    }
  };

  getAccessTabValue = (): string => {
    return this.isAnonymousAccessAllowed() ? "Anonymous" : "Restricted";
  };

  handleAccessTabChange = (value: string) => {
    if (value === "Anonymous") {
      if (!this.isAnonymousAccessAllowed()) {
        this.allowAnonymousAccessChanged();
      }
      return;
    }
    if (value === "Restricted" && this.isAnonymousAccessAllowed()) {
      this.allowAnonymousAccessChanged();
    }
  };

  getRolesSelectValues = (): Array<string> => {
    const authorization = this.getAuthorization();
    const permittedRoleIds = authorization.permittedRoles || [];
    const projectRoles = this.props.sd?.project?.roles || [];

    const validRoleIds = permittedRoleIds.filter((roleId) => projectRoles.some((role) => role.id === roleId));

    return validRoleIds;
  };

  handleRolesSelectChange = (value: any) => {
    const selectedValues: string[] = Array.isArray(value) ? (value as string[]) : [value];

    const authorization = this.getAuthorization();
    const current: string[] = authorization.permittedRoles || [];
    const next: string[] = selectedValues;

    const toAdd = next.filter((id) => !current.includes(id));
    const toRemove = current.filter((id) => !next.includes(id));

    const roles = this.props.sd?.project?.roles || [];
    toAdd.forEach((id) => {
      const role = roles.find((r) => r.id === id);
      if (role) this.authorizedRolesChanged(role, null);
    });
    toRemove.forEach((id) => {
      const role = roles.find((r) => r.id === id);
      if (role) this.authorizedRolesChanged(role, null);
    });
  };

  getHideDisableTabValue = (): string => {
    if (this.isHidden()) return "Hide";
    if (this.isDisabled()) return "Disable";
    return "Hide";
  };

  handleHideDisableTabChange = (value: string) => {
    if (value === "Hide") {
      if (!this.isHidden()) this.isHiddenChanged();
      if (this.isDisabled()) this.isDisabledChanged();
      return;
    }
    if (value === "Disable") {
      if (this.isHidden()) this.isHiddenChanged();
      if (!this.isDisabled()) this.isDisabledChanged();
    }
  };

  getAuthorization = (): IAuthorizationDto => {
    const sd = this.props.sd.getScreenDesign();
    let authorization: IAuthorizationDto | undefined;
    const api = this.props.sd.getSelectedApi();
    const apiMethod: IApiMethods | undefined = this.props.sd.getSelectedApiMethod();

    if (api && apiMethod) {
      if (apiMethod.authorization) {
        authorization = apiMethod.authorization;
      }
      if (!authorization) {
        authorization = this.defaultAuthorizationModel();
      }
      return authorization;
    }
    if (this.isScreen() === true) {
      authorization = sd.authorization;
    } else {
      const di = this.props.sd.screenDesignDictionary[parseInt(this.getKuikaId(), 10)];
      authorization = di?.authorization;
    }
    if (!authorization) {
      authorization = this.defaultAuthorizationModel();
    }
    return authorization;
  };

  defaultAuthorizationModel = () => {
    return {
      permittedRoles: [],
      hide: true,
      disable: false,
      allowAnonymousAccess: false,
      allowAllRoles: true
    } as IAuthorizationDto;
  };

  isRoleAuthorized = (role: IProjectRole): boolean => {
    const authorization = this.getAuthorization();
    return authorization.permittedRoles?.includes(role.id);
  };

  isHidden = (): boolean => {
    const authorization = this.getAuthorization();
    return authorization.hide;
  };

  isDisabled = (): boolean => {
    const authorization = this.getAuthorization();
    return authorization.disable;
  };

  isAnonymousAccessAllowed = (): boolean => {
    const authorization = this.getAuthorization();
    return authorization.allowAnonymousAccess;
  };

  areAllRolesAllowed = (): boolean => {
    const authorization = this.getAuthorization();
    return authorization.allowAllRoles;
  };

  getClassnameForActiveStatus = (a: { isActive: boolean }): string => {
    if (a.isActive) return "kuika_rotate90deg";
    return "kuika_rotate0deg";
  };

  handleRoleClick = () => {
    try {
      this.setState({
        isRoleModalActive: true
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  closeRoleModal = () => {
    this.setState({
      activeRoleId: "",
      activeRoleName: "",
      isRoleModalActive: false,
      activeStartingScreenId: "",
      isCheckingRoleUsage: false,
      roleUsageProjects: undefined,
      roleUsageCheckFailed: false
    });
  };

  // "No roles yet" in the roles list: the list closes and the New Role dialog takes over.
  openRoleModalFromEmptyList = (): void => {
    this.setState({ isRolesDropdownOpen: false });
    this.handleRoleClick();
  };

  // Saves the role. A role created here is also picked for this element: it was made to restrict
  // it, and unpicked it never reached the list below the select, so the add looked like it did
  // nothing. The dialog holds its spinner until this settles.
  handleChangeRole = async (data: IProjectRole): Promise<void> => {
    const isNewRole: boolean = !data.id;
    const roleId: string | undefined = await this.props.sd.updateRole(data);
    if (!isNewRole || !roleId || this.areAllRolesAllowed()) return;
    const role: IProjectRole | undefined = this.props.sd.project?.roles?.find((r: IProjectRole) => r.id === roleId);
    if (role && !this.isRoleAuthorized(role)) this.authorizedRolesChanged(role, null);
  };

  triggerDeleteRole = async (e: any, id: string, name: string) => {
    this.setState({ isLoading: true });
    const projectsThatUsedSameRoleName: string[] | undefined = await this.checkRoleIsDomainWide(name);
    this.setState({ isLoading: false });
    if (projectsThatUsedSameRoleName?.length) {
      this.setState({
        isDeleteRolePopConfirmVisible: true,
        activeEditDeletePopConfirmRoleId: id,
        projectsThatUsedSameRoleName
      });
      return;
    }
    // A failed check used to end here too, so Delete silently did nothing. The delete dialog still
    // asks for its typed confirmation; say what could not be checked and let it.
    if (projectsThatUsedSameRoleName === undefined) {
      KMainFunctions.displayWarningNotification("Couldn't check whether other projects use this role.");
    }
    this.handleDeleteRole(e, id, name);
  };

  handleDeleteRole = (_e: any, id: string, name: string) => {
    this.setState({
      activeEditDeletePopConfirmRoleId: null,
      isDeleteRolePopConfirmVisible: false,
      triggerScreenDeleteModal: true,
      roleToDeleteId: id,
      roleToDeleteName: name
    });
  };

  async deleteRole() {
    this.props.sd.deleteRole(this.state.roleToDeleteId);
  }

  /** The other projects of the domain that have a role of this name; undefined when the check failed. */
  checkRoleIsDomainWide = async (roleName: string): Promise<string[] | undefined> => {
    try {
      const projectId = this.props.sd.project.id;

      const res = await DomainService.GetDomainWideProjectRoles(window.userInfo?.kDomain_Id);
      if (res.status !== 200) return undefined;

      return res.data
        .filter((role) => role.projectId !== projectId && role.name == roleName)
        .map((role) => role.projectName);
    } catch (e: any) {
      KMainFunctions.exceptionHandler(e);
      return undefined;
    }
  };

  // The Edit dialog opens at once and asks which other projects share the role while it shows its
  // spinner — the question used to spin over the whole panel first, with nothing opening for seconds.
  // What it finds is said inside the dialog, above the form.
  editRole = async (id: string, name: string, startingScreenId: string): Promise<void> => {
    this.setState({
      activeEditDeletePopConfirmRoleId: null,
      activeRoleId: id,
      activeRoleName: name,
      activeStartingScreenId: startingScreenId,
      isRoleModalActive: true,
      isCheckingRoleUsage: true,
      roleUsageProjects: undefined,
      roleUsageCheckFailed: false
    });
    const projectsThatUsedSameRoleName: string[] | undefined = await this.checkRoleIsDomainWide(name);
    // Closed, or moved on to another role, while the check was out.
    if (!this.state.isRoleModalActive || this.state.activeRoleId !== id) return;
    this.setState({
      isCheckingRoleUsage: false,
      roleUsageProjects: projectsThatUsedSameRoleName,
      roleUsageCheckFailed: projectsThatUsedSameRoleName === undefined
    });
  };

  handleSearchInputChange = (e: any) => {
    this.setState({
      searchRoleKeyWords: e.target.value.split(" "),
      searchRoleText: e.target.value
    });
  };

  getRolePopConfirmTitle = (): string => {
    if (this.state.isDeleteRolePopConfirmVisible) return "Role Deletion Confirmation";
    return "";
  };

  // The consequence, under the question. It carries the confirmation's test id, which the E2E suite
  // uses to tell the two apart before pressing the right button.
  getRolePopConfirmDescription = (): React.ReactNode => {
    const projects: React.ReactNode = (
      <span className="projects-list">[{this.state.projectsThatUsedSameRoleName?.join(", ")}]</span>
    );
    if (this.state.isDeleteRolePopConfirmVisible)
      return (
        <span data-testid="roleDeleteConfirmationModal">
          This role is used in the following projects: {projects}. Deleting this role may affect these projects. Are you
          sure you want to proceed?
        </span>
      );
    return null;
  };

  toggleFolderExpansion = (folderId: string) => {
    this.setState((prevState) => ({
      expandedFolderIds: prevState.expandedFolderIds.includes(folderId)
        ? prevState.expandedFolderIds.filter((id) => id !== folderId)
        : [...prevState.expandedFolderIds, folderId]
    }));
  };

  isRoleInAnyFolder = (roleId: string, folders: any[]): boolean => {
    for (const f of folders || []) {
      if ((f.roleIds || []).includes(roleId)) return true;
      if (f.children && f.children.length && this.isRoleInAnyFolder(roleId, f.children)) return true;
    }
    return false;
  };

  getAllFolderIds = (folders: any[]): string[] => {
    const ids: string[] = [];
    for (const folder of folders || []) {
      ids.push(folder.id);
      if (folder.children && folder.children.length > 0) {
        ids.push(...this.getAllFolderIds(folder.children));
      }
    }
    return ids;
  };

  componentDidUpdate(prevProps: IAuthorizationPanelProps & IWithSDProps) {
    const currentRoleFolders = (this.props.sd?.project as any)?.roleFolders || [];
    const prevRoleFolders = (prevProps.sd?.project as any)?.roleFolders || [];

    // Update expanded folder IDs when role folders change
    if (JSON.stringify(currentRoleFolders) !== JSON.stringify(prevRoleFolders)) {
      this.setState({
        expandedFolderIds: this.getAllFolderIds(currentRoleFolders)
      });
    }
  }

  getTreeData = (): any[] => {
    const roles = this.props.sd?.project?.roles || [];
    const roleFolders = (this.props.sd?.project as any)?.roleFolders || [];

    const convertFolderToTreeNode = (folder: any): any => {
      const folderNode: any = {
        id: folder.id,
        name: folder.name,
        type: "folder",
        children: []
      };

      if (folder.roleIds && folder.roleIds.length > 0) {
        const folderRoles = folder.roleIds
          .map((roleId: string) => roles.find((r) => r.id === roleId))
          .filter(Boolean)
          .filter(
            (role: any) =>
              role?.name?.toLowerCase()?.includes(this.state.searchRoleText?.toLowerCase()) ||
              this.state.searchRoleText.length === 0
          )
          .map((role: any) => ({
            id: role.id,
            name: role.name,
            type: "role",
            value: role.id,
            label: role.name
          }));

        folderNode.children.push(...folderRoles);
      }

      // Add subfolders
      if (folder.children && folder.children.length > 0) {
        const subfolders = folder.children
          .filter(
            (f: any) =>
              f.name?.toLowerCase().includes(this.state.searchRoleText.toLowerCase()) ||
              this.state.searchRoleText.length === 0
          )
          .map(convertFolderToTreeNode);

        folderNode.children.push(...subfolders);
      }

      return folderNode;
    };

    const treeData: any[] = [];

    // Add folder structure
    const filteredFolders = roleFolders
      .filter(
        (f: any) =>
          f.name?.toLowerCase().includes(this.state.searchRoleText.toLowerCase()) ||
          this.state.searchRoleText.length === 0
      )
      .map(convertFolderToTreeNode);

    treeData.push(...filteredFolders);

    // Add roles not in any folder
    const unfolderRoles = roles
      .filter((role: any) => !this.isRoleInAnyFolder(role.id, roleFolders))
      .filter(
        (role: any) =>
          role?.name?.toLowerCase()?.includes(this.state.searchRoleText?.toLowerCase()) ||
          this.state.searchRoleText.length === 0
      )
      .map((role: any) => ({
        id: role.id,
        name: role.name,
        type: "role",
        value: role.id,
        label: role.name
      }));

    treeData.push(...unfolderRoles);

    return treeData;
  };

  renderRoleItem = (role: any, showActions: boolean = false) => {
    return (
      <KuikaPopconfirm
        key={role.id}
        position="top"
        align="end"
        // Opened from the row's Delete menu item when other projects share the role, never by
        // pressing the row itself, so the panel owns when it shows. (Edit says the same thing inside
        // its dialog instead.)
        open={
          showActions &&
          this.state.isDeleteRolePopConfirmVisible &&
          role.id === this.state.activeEditDeletePopConfirmRoleId
        }
        title={this.getRolePopConfirmTitle()}
        description={this.getRolePopConfirmDescription()}
        tone="danger"
        onConfirm={(e: React.MouseEvent<HTMLButtonElement>): void => {
          this.handleDeleteRole(e, role.id || "", role.name || "");
        }}
        onCancel={(): void => {
          this.setState({
            isDeleteRolePopConfirmVisible: false,
            activeEditDeletePopConfirmRoleId: null,
            projectsThatUsedSameRoleName: undefined
          });
        }}
        confirmLabel="Yes, Delete"
        cancelLabel="No, Cancel"
        confirmTestId="roleConfirmDeleteButton"
        cancelTestId="roleConfirmCancelButton"
      >
        <KuikaTooltip content={role.name}>
          <div className="kuika_roleList_item">
            <div className="kuika-flex-center--gap-s">
              <KuikaSvg name="key" width={12} height={12} />
              <KuikaSmallText>{role.name}</KuikaSmallText>
            </div>

            {showActions && (
              <KuikaDropdownMenu
                options={[
                  {
                    key: "1",
                    label: "Edit",
                    onClick: () => this.editRole(role.id || "", role.name || "", role.startingScreenId || "")
                  },
                  {
                    key: "2",
                    label: "Delete",
                    onClick: (e) => this.triggerDeleteRole(e, role.id || "", role.name || ""),
                    danger: true
                  }
                ]}
              >
                {(open) => (
                  <KuikaButton isIcon type="tertiary" color="neutral" active={open}>
                    <KuikaSvg data-testid={`${role.name}ThreeDotButton`} name="dots" />
                  </KuikaButton>
                )}
              </KuikaDropdownMenu>
            )}
          </div>
        </KuikaTooltip>
      </KuikaPopconfirm>
    );
  };

  renderFolderTree = (folders: any[], level = 0, showOnlySelected = false) => {
    return folders
      .filter((folder) => {
        if (showOnlySelected) {
          // Only show folders that contain selected roles
          const roles = this.props.sd?.project?.roles || [];
          const hasSelectedRoles = (folder.roleIds || []).some((roleId: string) => {
            const role = roles.find((r) => r.id === roleId);
            return role && this.isRoleAuthorized(role);
          });

          // Check if any child folders have selected roles
          const hasSelectedInChildren =
            folder.children && folder.children.length > 0 && this.hasSelectedRolesInFolders(folder.children);

          return hasSelectedRoles || hasSelectedInChildren;
        }

        return (
          folder.name?.toLowerCase().includes(this.state.searchRoleText.toLowerCase()) ||
          this.state.searchRoleText.length === 0
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((folder) => {
        const isExpanded = this.state.expandedFolderIds.includes(folder.id);
        const roles = this.props.sd?.project?.roles || [];

        return (
          <div key={folder.id}>
            <div className="auth-folder-header" onClick={() => this.toggleFolderExpansion(folder.id)}>
              <div className="auth-folder-title" style={{ paddingLeft: `${level * 24}px` }}>
                <KuikaSvg name="folder" type="sidebar-icons" width={12} height={12} />
                <KuikaSmallText>{folder.name}</KuikaSmallText>
              </div>
              <KuikaSvg name={isExpanded ? "chevron-down" : "chevron-right"} type="arrows" width={12} height={12} />
            </div>

            {isExpanded && (
              <div className="auth-folder-children">
                {(folder.roleIds || [])
                  .map((rid: string) => roles.find((r) => r.id === rid))
                  .filter(Boolean)
                  .filter((role: any) => {
                    if (showOnlySelected) {
                      return this.isRoleAuthorized(role);
                    }
                    return (
                      role?.name?.toLowerCase()?.includes(this.state.searchRoleText?.toLowerCase()) ||
                      this.state.searchRoleText.length === 0
                    );
                  })
                  .map((role: any) => (
                    <div
                      key={role.id}
                      className="auth-role-item-indent"
                      style={{ paddingLeft: `${(level + 1) * 24}px` }}
                    >
                      {this.renderRoleItem(role, true)}
                    </div>
                  ))}

                {folder.children &&
                  folder.children.length > 0 &&
                  this.renderFolderTree(folder.children, level + 1, showOnlySelected)}
              </div>
            )}
          </div>
        );
      });
  };

  hasSelectedRolesInFolders = (folders: any[]): boolean => {
    const roles = this.props.sd?.project?.roles || [];

    for (const folder of folders) {
      // Check if this folder has selected roles
      const hasSelectedRoles = (folder.roleIds || []).some((roleId: string) => {
        const role = roles.find((r) => r.id === roleId);
        return role && this.isRoleAuthorized(role);
      });

      if (hasSelectedRoles) return true;

      // Check child folders recursively
      if (folder.children && folder.children.length > 0) {
        if (this.hasSelectedRolesInFolders(folder.children)) return true;
      }
    }

    return false;
  };

  render() {
    if (!this.props || !this.props.sd) {
      return <></>;
    }
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    if (!selectedDisplayItem) {
      return <></>;
    }

    const api = this.props.sd.getSelectedApi();
    const apiMethod = this.props.sd.getSelectedApiMethod();

    return (
      <KuikaSpinnerSmall spinning={this.state.isLoading} size="small">
        <div className="authorization-panel">
          <div className="auth-tabs-row">
            <KuikaTabs
              options={["Anonymous", "Restricted"]}
              dataTestId="authAccessTabs"
              value={this.getAccessTabValue()}
              onChange={this.handleAccessTabChange}
            />
          </div>

          {this.getAccessTabValue() === "Restricted" && (
            <div className="auth-restricted-row">
              <div className="auth-everyone">
                <KuikaSmallText>Everyone</KuikaSmallText>
                <KuikaSwitch
                  data-testid="everyoneSwitch"
                  checked={this.areAllRolesAllowed()}
                  onChange={this.allowAllRolesChanged}
                />
              </div>

              {!this.areAllRolesAllowed() && (
                <div className="auth-roles-header">
                  <KuikaSmallMediumText>Roles</KuikaSmallMediumText>
                  <div className="auth-actions">
                    <KuikaSelectbox
                      placeholder="Select..."
                      mode="treeView"
                      value={this.getRolesSelectValues()}
                      onChange={this.handleRolesSelectChange}
                      searchable={false}
                      dropdownHeader={"Select roles"}
                      className="auth-roles-select"
                      data-testid="authorizationRolesSelect"
                      overlayClassName="auth-roles-select-dropdown"
                      treeData={this.getTreeData()}
                      expandedKeys={this.state.expandedFolderIds}
                      onExpandedKeysChange={(keys) => this.setState({ expandedFolderIds: keys })}
                      showCountOnly={true}
                      visible={this.state.isRolesDropdownOpen}
                      onVisibleChange={(isOpen: boolean): void => this.setState({ isRolesDropdownOpen: isOpen })}
                      notFoundContent={
                        <KuikaEmptyState
                          variant="embedded"
                          svgName="key"
                          name="Role"
                          customName="No roles yet."
                          action="Add"
                          description="to choose who can use this."
                          onDescriptionClick={this.openRoleModalFromEmptyList}
                        />
                      }
                    />
                    <KuikaButton
                      isIcon
                      type="tertiary"
                      color="neutral"
                      onClick={this.handleRoleClick}
                      aria-label="Add role"
                      data-testid="authorizationAddRoleButton"
                      data-onboarding-id="authorization-add-role"
                    >
                      <KuikaSvg width={16} height={16} name="plus" />
                    </KuikaButton>
                    <KuikaButton
                      isIcon
                      type="tertiary"
                      color="neutral"
                      onClick={() => this.setState({ isAccessManagementModalActive: true })}
                      aria-label="Manage roles"
                      data-onboarding-id="authorization-manage-roles"
                    >
                      <KuikaSvg width={16} height={16} name="settings" type="sidebar-icons" />
                    </KuikaButton>
                  </div>
                </div>
              )}

              {!this.areAllRolesAllowed() && (
                <>
                  <div className="auth-roles-list">
                    <div className="auth-roles-list-inner">
                      {(() => {
                        const roles = this.props.sd?.project?.roles || [];
                        const roleFolders = (this.props.sd?.project as any)?.roleFolders || [];
                        const selectedCount = (this.getAuthorization().permittedRoles || []).length;
                        if (selectedCount === 0) {
                          return (
                            <div className="auth-roles-empty" aria-label="No roles selected" tabIndex={0}>
                              <KuikaSmallText>No roles selected</KuikaSmallText>
                            </div>
                          );
                        }
                        return (
                          <>
                            {this.renderFolderTree(roleFolders, 0, true)}
                            {roles
                              .filter((role) => !this.isRoleInAnyFolder(role.id, roleFolders))
                              .filter((role) => this.isRoleAuthorized(role))
                              .map((role) => this.renderRoleItem(role, true))}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </>
              )}

              {!api && !apiMethod && !this.areAllRolesAllowed() && (
                <div>
                  <KuikaSmallText className="auth-hint">
                    When the user is not authorized choose to hide or disable
                  </KuikaSmallText>
                  <div className="auth-choose-row">
                    <KuikaSmallText>Choose</KuikaSmallText>
                    <KuikaTabs
                      options={["Hide", "Disable"]}
                      value={this.getHideDisableTabValue()}
                      onChange={this.handleHideDisableTabChange}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DeleteModal
          itemName={this.state.roleToDeleteName}
          triggerModal={this.state.triggerScreenDeleteModal}
          deleteItem={() => this.deleteRole()}
          deleteType={"Role"}
          closeModal={() =>
            this.setState({
              triggerScreenDeleteModal: false,
              roleToDeleteName: ""
            })
          }
        />
        <RoleModal
          showModal={this.state.isRoleModalActive}
          closeModal={this.closeRoleModal}
          onChange={this.handleChangeRole}
          id={this.state.activeRoleId}
          name={this.state.activeRoleName}
          startingScreenId={this.state.activeStartingScreenId}
          isCheckingUsage={this.state.isCheckingRoleUsage}
          usedInProjects={this.state.roleUsageProjects}
          usageCheckFailed={this.state.roleUsageCheckFailed}
        ></RoleModal>
        <RoleManagementModal
          showModal={this.state.isAccessManagementModalActive}
          closeModal={() => this.setState({ isAccessManagementModalActive: false })}
        />
      </KuikaSpinnerSmall>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.UpdateAuth ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi
    ) {
      return true;
    }
    return false;
  };
}

const authorizationPanel = withSD<IAuthorizationPanelProps>(AuthorizationPanel);
export { authorizationPanel as AuthorizationPanel };
